import React, { useState } from 'react';
import { useAsignacion } from '../hooks/useAsignacion';
import { FormSelect, MultiSelectCards } from '../components/form/FormFields';
import { UsuarioListItem } from '../types/usuario.types';

// ============================================================
// AsignacionFormPage
// – Muestra las asignaciones actuales del docente cargadas del backend.
// – Permite agregar nuevas asignaciones o eliminar existentes.
// ============================================================

interface Props {
  docente: UsuarioListItem;
  onSuccess?: () => void;
  onCancel?:  () => void;
}

const SpinnerIcon = ({ cls = 'w-5 h-5 text-slate-400' }: { cls?: string }) => (
  <svg className={`animate-spin ${cls}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Fila con orden editable inline ────────────────────────────
interface OrdenRowProps {
  idAsignacion: number;
  cursoNombre:  string;
  orden:        number;
  onOrdenChange: (id: number, orden: number) => Promise<void> | void;
  onEliminar:   (id: number) => void;
}
const OrdenRow: React.FC<OrdenRowProps> = ({ idAsignacion, cursoNombre, orden, onOrdenChange, onEliminar }) => {
  const [val, setVal]       = useState(orden);
  const [saving, setSaving] = useState(false);
  const dirty = val !== orden && val >= 1 && val <= 99;

  const guardar = async () => {
    if (!dirty) return;
    setSaving(true);
    try { await onOrdenChange(idAsignacion, val); }
    finally { setSaving(false); }
  };

  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-5 py-2">
        <div className="flex items-center gap-1.5 justify-center">
          <input
            type="number"
            value={val}
            min={1}
            max={99}
            onChange={e => setVal(Number(e.target.value))}
            onKeyDown={e => { if (e.key === 'Enter') guardar(); }}
            className={`w-12 text-center text-xs font-bold rounded-lg border py-1 outline-none
              focus:ring-2 focus:ring-blue-500 transition-all
              ${val === 99
                ? 'bg-slate-100 text-slate-400 border-slate-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'}`}
          />
          {/* Botón guardar — solo visible cuando el valor cambió */}
          {dirty && (
            <button
              onClick={guardar}
              disabled={saving}
              title="Guardar orden"
              className="w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 text-white
                         flex items-center justify-center transition-colors disabled:opacity-60"
            >
              {saving
                ? <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/></svg>
                : <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/></svg>
              }
            </button>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-sm font-medium text-slate-700">{cursoNombre}</td>
      <td className="px-5 py-2">
        <button
          onClick={() => onEliminar(idAsignacion)}
          title="Eliminar asignación"
          className="text-slate-300 hover:text-red-500 transition-colors text-lg leading-none">
          ×
        </button>
      </td>
    </tr>
  );
};

// ── Referencia CNEB ───────────────────────────────────────────
const CNEB: Record<string, { orden: number; area: string }[]> = {
  INICIAL: [
    { orden: 1, area: 'Comunicación' },
    { orden: 2, area: 'Castellano como segunda lengua *' },
    { orden: 3, area: 'Personal Social' },
    { orden: 4, area: 'Psicomotriz' },
    { orden: 5, area: 'Ciencia y Tecnología' },
    { orden: 6, area: 'Matemática' },
  ],
  PRIMARIA: [
    { orden: 1, area: 'Comunicación' },
    { orden: 2, area: 'Castellano como segunda lengua *' },
    { orden: 3, area: 'Inglés' },
    { orden: 4, area: 'Arte y Cultura' },
    { orden: 5, area: 'Personal Social' },
    { orden: 6, area: 'Educación Religiosa' },
    { orden: 7, area: 'Educación Física' },
    { orden: 8, area: 'Ciencia y Tecnología' },
    { orden: 9, area: 'Matemática' },
  ],
  SECUNDARIA: [
    { orden: 1,  area: 'Comunicación' },
    { orden: 2,  area: 'Castellano como segunda lengua *' },
    { orden: 3,  area: 'Inglés' },
    { orden: 4,  area: 'Arte y Cultura' },
    { orden: 5,  area: 'Desarrollo Personal, Ciudadanía y Cívica' },
    { orden: 6,  area: 'Ciencias Sociales' },
    { orden: 7,  area: 'Educación Religiosa' },
    { orden: 8,  area: 'Educación Física' },
    { orden: 9,  area: 'Ciencia y Tecnología' },
    { orden: 10, area: 'Educación para el Trabajo' },
    { orden: 11, area: 'Matemática' },
  ],
};

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-50  border-amber-200  text-amber-800',
  PRIMARIA:   'bg-blue-50   border-blue-200   text-blue-800',
  SECUNDARIA: 'bg-purple-50 border-purple-200 text-purple-800',
};

const RefCneb: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden mb-5">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50
                   hover:bg-slate-100 transition-colors text-left"
      >
        <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
          📋 Referencia orden CNEB por nivel
        </span>
        <svg
          viewBox="0 0 20 20" fill="currentColor"
          className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"/>
        </svg>
      </button>

      {open && (
        <div className="p-5 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(CNEB).map(([nivel, areas]) => (
              <div key={nivel} className={`rounded-xl border p-3 ${NIVEL_COLOR[nivel]}`}>
                <p className="text-xs font-extrabold uppercase tracking-wide mb-2">{nivel}</p>
                <ol className="space-y-1">
                  {areas.map(({ orden, area }) => (
                    <li key={orden} className="flex gap-2 text-xs">
                      <span className="font-bold w-5 text-right flex-shrink-0 opacity-60">{orden}.</span>
                      <span className={area.endsWith('*') ? 'opacity-50 italic' : ''}>{area}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-3">
            * Castellano como segunda lengua solo aplica en colegios EIB (Educación Intercultural Bilingüe).
          </p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
const AsignacionFormPage: React.FC<Props> = ({ docente, onSuccess = () => {}, onCancel = () => {} }) => {
  const {
    form, errors, submitting, submitError, loading,
    asignacionesActuales,
    añosEscolares, grados, secciones, cursos,
    handleChange, toggleCurso, handleSubmit, eliminarAsignacion, actualizarOrden,
  } = useAsignacion(docente.idUsuario, onSuccess);

  // Agrupar asignaciones actuales por año+grado+sección
  const grupos = asignacionesActuales.reduce<Record<string, typeof asignacionesActuales>>((acc, a) => {
    const key = `${a.anio} · ${a.gradoNombre} (${a.gradoNivel}) · Sección ${a.seccionNombre}`;
    acc[key] = acc[key] ? [...acc[key], a] : [a];
    return acc;
  }, {});

  return (
    <div className="max-w-2xl mx-auto">

      {/* Cabecera */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onCancel}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Asignación de Cursos</h1>
          <p className="text-slate-400 text-sm">
            {loading ? 'Consultando datos del backend...' : `${asignacionesActuales.length} asignación${asignacionesActuales.length !== 1 ? 'es' : ''} registrada${asignacionesActuales.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Card del docente */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 rounded-2xl p-5 mb-5 flex items-center gap-4 shadow-md">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center
                        text-white font-bold text-lg flex-shrink-0">
          {docente.nombres.charAt(0)}{docente.apellidos.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold">{docente.nombres} {docente.apellidos}</p>
          <p className="text-blue-200 text-sm">DNI: {docente.dni} · @{docente.username}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-blue-300 text-xs">Asignaciones</p>
          <p className="text-white font-bold text-xl leading-none">
            {loading ? '…' : asignacionesActuales.length}
          </p>
        </div>
      </div>

      {/* ── Referencia CNEB ── */}
      <RefCneb />

      {/* ── Asignaciones actuales ── */}
      {!loading && Object.keys(grupos).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-5 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
              Asignaciones actuales
            </p>
            <p className="text-xs text-slate-400">
              Cambia el <b>#</b> → aparece ✓ para guardar
            </p>
          </div>
          <div className="divide-y divide-slate-50">
            {Object.entries(grupos).map(([grupo, items]) => (
              <div key={grupo}>
                <p className="px-5 pt-3 pb-1 text-xs font-bold text-slate-500">{grupo}</p>
                <table className="w-full">
                  <thead>
                    <tr className="text-[10px] uppercase text-slate-400 font-bold">
                      <th className="px-5 py-1 text-center w-16">#</th>
                      <th className="px-3 py-1 text-left">Curso</th>
                      <th className="px-5 py-1 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...items].sort((a, b) => (a.orden ?? 99) - (b.orden ?? 99)).map(a => (
                      <OrdenRow
                        key={a.idAsignacion}
                        idAsignacion={a.idAsignacion}
                        cursoNombre={a.cursoNombre}
                        orden={a.orden ?? 99}
                        onOrdenChange={actualizarOrden}
                        onEliminar={eliminarAsignacion}
                      />
                    ))}
                  </tbody>
                </table>
                <div className="pb-2" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Formulario para nueva asignación ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h2 className="text-white font-bold">
            {asignacionesActuales.length > 0 ? 'Agregar Asignación' : 'Nueva Asignación'}
          </h2>
          <p className="text-blue-200 text-xs mt-0.5">
            Se enviará una petición por cada curso seleccionado
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
            <SpinnerIcon />
            <span className="text-sm">Consultando datos del backend...</span>
          </div>
        ) : (
          <div className="p-6 space-y-4">

            {/* Año Escolar */}
            <FormSelect
              label="Año Escolar"
              name="anioEscolarId"
              value={form.anioEscolarId}
              onChange={handleChange}
              error={errors.anioEscolarId}
              required
              placeholder="Seleccionar año escolar"
              options={añosEscolares.map(a => ({ value: a.idAnioEscolar, label: `${a.anio} (Activo)` }))}
            />

            {/* Grado + Sección */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormSelect
                label="Grado"
                name="gradoId"
                value={form.gradoId}
                onChange={handleChange}
                error={errors.gradoId}
                required
                placeholder="Seleccionar grado"
                options={grados.map(g => ({ value: g.idGrado, label: `${g.nombre} – ${g.nivel}` }))}
              />
              <FormSelect
                label="Sección"
                name="seccionId"
                value={form.seccionId}
                onChange={handleChange}
                error={errors.seccionId}
                required
                placeholder="Seleccionar sección"
                options={secciones.map(s => ({ value: s.idSeccion, label: `Sección ${s.nombre}` }))}
              />
            </div>

            {/* Cursos – multi-select con cursos del backend */}
            <MultiSelectCards
              label="Cursos a Asignar"
              required
              error={errors.cursosIds}
              items={cursos.map(c => ({
                id:       c.idCurso,
                label:    c.nombre,
                sublabel: c.descripcion,
              }))}
              selected={form.cursosIds}
              onToggle={toggleCurso}
            />

            {submitError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                </svg>
                <span className="text-red-600 text-sm">{submitError}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting || loading}
            className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold
                       bg-blue-700 hover:bg-blue-800 text-white shadow-sm
                       disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
            {submitting ? <SpinnerIcon cls="w-4 h-4 text-white" /> : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            {form.cursosIds.length > 1
              ? `Guardar ${form.cursosIds.length} asignaciones`
              : 'Guardar Asignación'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AsignacionFormPage;